import {
  ConfirmationDocument,
  useVbsConfirmationPatchMutation,
  useVbsConfirmationPdfPreviewQuery,
  useVbsConfirmationShowQuery,
} from "@/services/backend/vbs/confirmations/api/confirmation-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsHeader,
  DocumentSheetContent,
  DocumentSheetContextProvider,
  DocumentSheetHeader,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
  useDocumentSheetContext,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import t from "@/lang/lang";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { TreatmentComponent } from "@/services/backend/vbs/treatments/components/treatment-component";
import { Column } from "@/services/backend/vbs/treatments/lib/treatment-component-state";
import { CalculationComponent } from "@/routes/processes/[processId]/vbs/calculations/[calculationId]/calculation/calculation-route";
import { ServiceAddressCardWithMutation } from "@/services/backend/addresses/components/service-address-card";
import { RepresentativeCardWithMutation } from "@/services/backend/representatives/components/representative-card";
import { Button } from "@/shared/components/ui/button";
import { Confirmation } from "@/services/backend/vbs/confirmations/types/confirmation";
import {
  DocumentActionsDownload,
  DocumentActionsHeader,
} from "@/shared/bsm/core/document/components/ui/document-immutable-page";
import { DownloadButton } from "@/routes/processes/[processId]/vbs/confirmations/[confirmationId]/_components/confirmation-download-buttons";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { NavigateToDocumentButton } from "@/services/backend/documents/components/preview/_components/navigate-to-document-button";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { RefreshCw } from "lucide-react";
import { ConfirmationDataView } from "@/routes/processes/[processId]/vbs/confirmations/[confirmationId]/data/confirmation-data-route";
import {
  DocumentPage,
  DocumentPageContent,
  DocumentPageHeader,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { PostalAddressCardWithMutation } from "@/services/backend/addresses/components/postal-address-card";
import { isImmutable } from "@/services/backend/documents/lib/document-state";

export function VbsConfirmationPreview({
  confirmationId,
}: {
  confirmationId: string;
}) {
  const {
    data: confirmation,
    isLoading,
    error,
  } = useVbsConfirmationShowQuery({ id: confirmationId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return <RefreshCw className="h-4 w-4" />;
  }

  return (
    <DocumentSheetContextProvider>
      <ConfirmationDocumentSheet confirmation={confirmation!} />
    </DocumentSheetContextProvider>
  );
}

function ConfirmationDocumentSheet({
  confirmation,
}: {
  confirmation: Confirmation;
}) {
  const { open, setOpen } = useDocumentSheetContext();

  return (
    <DocumentSheet open={open} onOpenChange={setOpen}>
      <DocumentSheetTrigger asChild>
        <Button variant="ghost" size="sm">
          {t("Vorschau")}
        </Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>
            {t(confirmation!.documentKind)} {t("Vorschau")}
          </DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          {isImmutable(confirmation!) ? (
            <ImmutableConfirmationPreview confirmation={confirmation!} />
          ) : (
            <MutableConfirmationPreview confirmation={confirmation!} />
          )}
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function ImmutableConfirmationPreview({
  confirmation,
}: {
  confirmation: Confirmation;
}) {
  const { data, isLoading, isFetching, error } =
    useVbsConfirmationPdfPreviewQuery(
      {
        id: confirmation.id,
        document: ConfirmationDocument.All,
      },
      // Rendered inside the sheet's content, so it mounts when the sheet
      // opens: refetch then, showing the last PDF until the fresh one arrives.
      { refetchOnMountOrArgChange: true },
    );

  return (
    <>
      <DocumentSheetPreview
        isLoading={isLoading}
        isFetching={isFetching}
        objectURL={data?.objectURL}
        error={error}
      />
      <ImmutableConfirmationActions confirmation={confirmation} />
    </>
  );
}

function ImmutableConfirmationActions({
  confirmation,
}: {
  confirmation: Confirmation;
}) {
  return (
    <DocumentSheetActions>
      <DocumentActionsHeader
        title={t(confirmation.documentKind)}
        documentNumber={
          <ReferenceNumber referenceNumber={confirmation.referenceNumber} />
        }
      />
      <DocumentSheetActionsContent>
        <DocumentActionsDownload>
          <DownloadButton
            confirmationId={confirmation.id}
            document={ConfirmationDocument.All}
            text={t("Alle Dokumente")}
          />
          <DownloadButton
            confirmationId={confirmation.id}
            document={ConfirmationDocument.Letter}
            text={t("Angebot")}
          />
          <DownloadButton
            confirmationId={confirmation.id}
            document={ConfirmationDocument.Treatment}
            text={t("Leistungsverzeichnis")}
          />
        </DocumentActionsDownload>
      </DocumentSheetActionsContent>
    </DocumentSheetActions>
  );
}

function MutableConfirmationPreview({
  confirmation,
}: {
  confirmation: Confirmation;
}) {
  return (
    <>
      <MutableConfirmationView confirmation={confirmation} />
      <MutableConfirmationActions confirmation={confirmation} />
    </>
  );
}

function MutableConfirmationView({
  confirmation,
}: {
  confirmation: Confirmation;
}) {
  return (
    <div className="md:col-span-4">
      <PersistenceContextProvider>
        <DocumentPage>
          <Tabs defaultValue="data">
            <DocumentPageHeader>
              <TabsList>
                <TabsTrigger value="data">{t("Bestätigung")}</TabsTrigger>
                <TabsTrigger value="treatment">{t("Leistungen")}</TabsTrigger>
                <TabsTrigger value="calculation">
                  {t("Kalkulation")}
                </TabsTrigger>
                <TabsTrigger value="customer-address">
                  {t("Anschrift")}
                </TabsTrigger>
                <TabsTrigger value="representative">
                  {t("Betreuer")}
                </TabsTrigger>
              </TabsList>
              <PersistenceStateUsingContext className="mr-2 ml-auto" />
            </DocumentPageHeader>
            <DocumentPageContent className="max-h-[75vh] overflow-auto">
              <TabsContent value="data">
                <ConfirmationDataView confirmation={confirmation} />
              </TabsContent>
              <TabsContent value="treatment">
                <TreatmentComponent
                  treatmentId={confirmation.treatmentId}
                  availableColumns={[
                    Column.Position,
                    Column.ComponentKind,
                    Column.ComponentData,
                    Column.Cycle,
                    Column.Formula,
                    Column.CleanTime,
                    Column.BasePrice,
                    Column.CycleDiscount,
                    Column.SpecialDiscount,
                    Column.FinalPrice,
                    Column.Comment,
                    Column.Actions,
                  ]}
                  defaultColumns={[
                    Column.Position,
                    Column.ComponentKind,
                    Column.BasePrice,
                    Column.CycleDiscount,
                    Column.FinalPrice,
                    Column.Comment,
                    Column.Actions,
                  ]}
                />
              </TabsContent>
              <TabsContent value="calculation">
                <CalculationComponent
                  calculationId={confirmation.calculationId}
                />
              </TabsContent>
              <TabsContent value="customer-address">
                <div className="space-y-4">
                  <PostalAddressCardWithMutation
                    id={confirmation!.id}
                    address={confirmation!.postalAddress}
                    mutation={useVbsConfirmationPatchMutation}
                  />
                  <ServiceAddressCardWithMutation
                    id={confirmation!.id}
                    address={confirmation!.serviceAddress}
                    mutation={useVbsConfirmationPatchMutation}
                  />
                </div>
              </TabsContent>
              <TabsContent value="representative">
                <RepresentativeCardWithMutation
                  id={confirmation!.id}
                  representative={confirmation!.representative}
                  mutation={useVbsConfirmationPatchMutation}
                />
              </TabsContent>
            </DocumentPageContent>
          </Tabs>
        </DocumentPage>
      </PersistenceContextProvider>
    </div>
  );
}

function MutableConfirmationActions({
  confirmation,
}: {
  confirmation: Confirmation;
}) {
  return (
    <DocumentSheetActions className="md:col-span-1">
      <DocumentSheetActionsHeader
        title={t(confirmation.documentKind)}
        documentNumber={
          <ReferenceNumber referenceNumber={confirmation.referenceNumber} />
        }
      />
      <DocumentSheetActionsContent>
        <NavigateToDocumentButton
          documentId={confirmation.id}
          documentKind={confirmation.documentKind}
          processId={confirmation.processId}
          serviceKind={GSMServiceKind.VBS}
        />
      </DocumentSheetActionsContent>
    </DocumentSheetActions>
  );
}
